import React, { useState, useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import {
  Users,
  UtensilsCrossed,
  Car,
  Building,
  AirVent,
  ChefHat,
  Church,
  Wifi,
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle2,
  Accessibility,
  Maximize2,
  Home as HomeIcon,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  Loader2,
} from 'lucide-react';

// Web3Forms Access Key for contact form submissions
const WEB3FORMS_ACCESS_KEY = '19b8464e-9053-4579-af14-c48c419f8bcb';
// Web3Forms shared hCaptcha sitekey (per Web3Forms documentation)
const HCAPTCHA_SITE_KEY = '50b2fe65-b00b-4b9e-ad62-3ba471098be2';

// Image optimization helper - uses wsrv.nl (free image CDN/proxy)
// Supports: width, height, quality, format conversion
const optimizeImage = (src, options = {}) => {
  const { width, height, quality = 80, format = 'webp' } = options;
  
  // wsrv.nl is a free, open-source image optimization service
  const params = new URLSearchParams();
  if (width) params.append('w', width);
  if (height) params.append('h', height);
  params.append('q', quality);
  params.append('output', format);
  params.append('fit', 'cover');
  
  return `https://wsrv.nl/?url=${encodeURIComponent(src)}&${params.toString()}`;
};

// Optimized Image Component with blur placeholder
const OptimizedImage = ({ src, alt, className, width, height, quality, priority = false, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  // Generate optimized URLs
  const optimizedSrc = optimizeImage(src, { width, height, quality });
  const placeholderSrc = optimizeImage(src, { width: 20, height: 20, quality: 30 });
  
  return (
    <div className={`relative overflow-hidden ${className}`} onClick={onClick}>
      {/* Blur placeholder */}
      {!loaded && !error && (
        <img
          src={placeholderSrc}
          alt=""
          width={width}
          height={height}
          className="absolute inset-0 w-full h-full object-cover blur-lg scale-110"
          aria-hidden="true"
        />
      )}
      {/* Main image */}
      <img
        src={error ? src : optimizedSrc}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  );
};

// Default/fallback gallery images if database is empty
const defaultGalleryImages = [
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/3mnrjx72_Entrance.jpg', alt: 'Entrance' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/xzr2wv3d_Main%20Hall%20Entrance.jpg', alt: 'Main Hall Entrance' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/pnhuoqga_Main%20Hall%20-%201.jpg', alt: 'Main Hall - 1' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/u6c2r4ov_Main%20Hall%20-%202.jpg', alt: 'Main Hall - 2' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/ysk9myb0_Main%20Hall%20-%203.jpg', alt: 'Main Hall - 3' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/16lmcphe_Buffet%20Hall.jpg', alt: 'Buffet Hall' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/32n1ccj1_Buffet%20Hall%20-2.jpg', alt: 'Buffet Hall - 2' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/p4gz0azy_Prefunction%20Hall.jpg', alt: 'Prefunction Hall' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/rter8iwd_Portico.jpg', alt: 'Portico' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/gw3jef5s_Bride%20%26%20Groom%20Room%20Bedroom.jpg', alt: 'Bride & Groom Room Bedroom' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/m50g19tk_Bride%20%26%20Groom%20Room%20Dressing.jpg', alt: 'Bride & Groom Room Dressing' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/7iyw1lzj_Bride%20%26%20Groom%20Room%20Hall.jpg', alt: 'Bride & Groom Room Hall' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/6ju6yurt_Guest%20Room%20-%201.jpg', alt: 'Guest Room - 1' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/ikllz90g_Guest%20Room%20-%202.jpg', alt: 'Guest Room - 2' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/23s7ddvp_Exterior%20View%20of%20Parking.jpg', alt: 'Exterior View of Parking' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/xk3kv1d5_Parking%20-1.jpg', alt: 'Parking - 1' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/0p09xt9z_Parking%20-2.jpg', alt: 'Parking - 2' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/efauaom6_Parking%20-%203.jpg', alt: 'Parking - 3' },
  { src: 'https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/yq8z1bp5_Temple.jpg', alt: 'Temple' },
];

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    message: '',
  });
  const [hcaptchaToken, setHcaptchaToken] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const hcaptchaRef = useRef(null);
  const contactSectionRef = useRef(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAllImages, setShowAllImages] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Use hardcoded gallery images for static website
  const galleryImages = defaultGalleryImages;

  const displayedImages = showAllImages ? galleryImages : galleryImages.slice(0, 9);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  // Lazy-mount hCaptcha only when the contact section nears the viewport.
  // This defers loading hCaptcha's ~100KB JS until the user actually needs it.
  React.useEffect(() => {
    if (showCaptcha) return;
    const el = contactSectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setShowCaptcha(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShowCaptcha(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [showCaptcha]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate hCaptcha token
    if (!hcaptchaToken) {
      toast({
        title: 'Captcha Required',
        description: 'Please complete the captcha verification.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    // Submit to Web3Forms using FormData (better CORS handling)
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('access_key', WEB3FORMS_ACCESS_KEY);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('event_date', formData.date || 'Not specified');
      formDataToSend.append('message', formData.message || 'No additional message');
      formDataToSend.append('subject', `New Inquiry from ${formData.name} - Selvam Mahaal`);
      formDataToSend.append('from_name', 'Selvam Mahaal Website');
      formDataToSend.append('h-captcha-response', hcaptchaToken);

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: 'Inquiry Submitted!',
          description: 'Thank you! We will contact you shortly.',
        });
        setFormData({ name: '', email: '', phone: '', date: '', message: '' });
        setHcaptchaToken('');
        if (hcaptchaRef.current) {
          hcaptchaRef.current.resetCaptcha();
        }
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: 'Submission Failed',
        description: 'Please try again later or contact us directly.',
        variant: 'destructive',
      });
      setHcaptchaToken('');
      if (hcaptchaRef.current) {
        hcaptchaRef.current.resetCaptcha();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-[#FFF8F0]">
      {/* Header */}
      <header className="bg-[#5C4033] text-white sticky top-0 z-50 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <img
              src={optimizeImage('https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/bkx7dojo_Logo%20White.png', { width: 160, quality: 85 })}
              alt="Selvam Mahaal Logo"
              width="160"
              height="64"
              fetchPriority="high"
              decoding="async"
              className="h-16 w-auto object-contain"
            />
          </div>
          <nav className="hidden md:flex space-x-8">
            <a href="#home" className="hover:text-[#D4AF37] transition-colors duration-300 font-medium">
              Home
            </a>
            <a href="#about" className="hover:text-[#D4AF37] transition-colors duration-300 font-medium">
              About
            </a>
            <a href="#facilities" className="hover:text-[#D4AF37] transition-colors duration-300 font-medium">
              Facilities
            </a>
            <a href="#gallery" className="hover:text-[#D4AF37] transition-colors duration-300 font-medium">
              Gallery
            </a>
            <a href="#contact" className="hover:text-[#D4AF37] transition-colors duration-300 font-medium">
              Contact
            </a>
          </nav>
          <a href="tel:+919688549999" className="flex items-center space-x-2 hover:text-[#D4AF37] transition-colors duration-300">
            <Phone className="w-4 h-4" />
            <span className="text-sm font-medium">+91 96885 49999</span>
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative h-[600px] overflow-hidden">
        <img
          src={optimizeImage('https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/r3x5euza_selvam-mahaal-eachanari-coimbatore.jpg', { width: 1920, height: 600, quality: 75 })}
          alt="Selvam Mahaal wedding venue exterior"
          width="1920"
          height="600"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#5C4033]/60 to-[#5C4033]/30"></div>
        <div className="relative container mx-auto px-4 h-full flex items-center">
          <div className="text-white max-w-2xl">
            <h2 className="text-5xl md:text-6xl font-bold mb-4 leading-tight">
              Create Unforgettable Memories
            </h2>
            <p className="text-xl mb-8 text-gray-100">
              Premium Wedding & Event Hall in the Heart of Coimbatore
            </p>
            <Button
              size="lg"
              className="bg-[#D4AF37] hover:bg-[#B8941F] text-[#5C4033] font-semibold px-8 py-6 text-lg transition-all duration-300 transform hover:scale-105"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Book Your Event
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white shadow-lg -mt-20 relative z-10 mx-4 md:mx-8 rounded-lg">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            <div className="text-center p-6 border-r border-gray-200">
              <Users className="w-12 h-12 mx-auto mb-4 text-[#D4AF37]" />
              <h3 className="text-4xl font-bold text-[#5C4033] mb-2">1500</h3>
              <p className="text-gray-600 font-medium">Main Hall Seating Capacity</p>
            </div>
            <div className="text-center p-6 border-r border-gray-200">
              <UtensilsCrossed className="w-12 h-12 mx-auto mb-4 text-[#D4AF37]" />
              <h3 className="text-4xl font-bold text-[#5C4033] mb-2">600</h3>
              <p className="text-gray-600 font-medium">Dining Hall Capacity</p>
            </div>
            <div className="text-center p-6 border-r border-gray-200">
              <Car className="w-12 h-12 mx-auto mb-4 text-[#D4AF37]" />
              <h3 className="text-4xl font-bold text-[#5C4033] mb-2">350</h3>
              <p className="text-gray-600 font-medium">Car Parkings in Campus</p>
            </div>
            <div className="text-center p-6 border-r border-gray-200">
              <Building className="w-12 h-12 mx-auto mb-4 text-[#D4AF37]" />
              <h3 className="text-4xl font-bold text-[#5C4033] mb-2">16</h3>
              <p className="text-gray-600 font-medium">Fully A/c Guest Rooms</p>
            </div>
            <div className="text-center p-6">
              <Maximize2 className="w-12 h-12 mx-auto mb-4 text-[#D4AF37]" />
              <h3 className="text-4xl font-bold text-[#5C4033] mb-2">11000</h3>
              <p className="text-gray-600 font-medium">Sq Ft Separate Covered Buffet Space</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-[#FFF8F0]">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#5C4033] mb-6">
                Welcome to Selvam Mahaal
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Located in Coimbatore, Selvam Mahaal stands as a premier destination for
                celebrating life's most precious moments. With state-of-the-art facilities and
                traditional charm, we provide the perfect venue for your special occasions.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Our spacious halls, modern amenities, and dedicated service ensure that every event
                hosted at our venue becomes an unforgettable experience. From grand weddings to
                corporate events, we cater to all your celebration needs.
              </p>
              <Button
                className="bg-[#5C4033] hover:bg-[#4A3329] text-white px-8 py-6 text-lg transition-all duration-300"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </div>
            <div className="relative">
              <img
                src={optimizeImage('https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/eas08n2w_Gemini_Generated_Image_93jpc693jpc693jp.png', { width: 600, height: 500, quality: 75 })}
                alt="Wedding celebration"
                width="600"
                height="500"
                loading="lazy"
                decoding="async"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] text-white p-6 rounded-lg shadow-xl">
                <p className="text-sm font-medium">Creating Memories Since</p>
                <p className="text-3xl font-bold">2018</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section id="facilities" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#5C4033] mb-4">Our Premium Facilities</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-[#FFF8F0] rounded-full flex items-center justify-center mx-auto mb-6">
                  <AirVent className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#5C4033] mb-4">Centrally Air Conditioned</h3>
                <p className="text-gray-600 leading-relaxed">
                  Fully air-conditioned halls ensuring comfort for you and your guests throughout the
                  event, regardless of the season.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-[#FFF8F0] rounded-full flex items-center justify-center mx-auto mb-6">
                  <ChefHat className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#5C4033] mb-4">Separate Buffet Hall</h3>
                <p className="text-gray-600 leading-relaxed">
                  Dedicated RC covered buffet area of 11000 sq ft providing ample space for dining arrangements and guest comfort.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-[#FFF8F0] rounded-full flex items-center justify-center mx-auto mb-6">
                  <UtensilsCrossed className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#5C4033] mb-4">VIP Dining</h3>
                <p className="text-gray-600 leading-relaxed">
                  Dedicated VIP private dining area that seats 30 people, perfect for intimate gatherings and special guests.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-[#FFF8F0] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Church className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#5C4033] mb-4">Temple Within Premises</h3>
                <p className="text-gray-600 leading-relaxed">
                  On-site temple facility for traditional ceremonies and religious functions with
                  peaceful ambiance.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-[#FFF8F0] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#5C4033] mb-4">Prefunction Area</h3>
                <p className="text-gray-600 leading-relaxed">
                  Spacious prefunction area accommodating 600 people, ideal for rituals, music events and guest reception.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-[#FFF8F0] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Accessibility className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#5C4033] mb-4">Elder Friendly</h3>
                <p className="text-gray-600 leading-relaxed">
                  Ramps at all entrances and lift access to the first floor. Wheelchair available on request for your convenience.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-[#FFF8F0] rounded-full flex items-center justify-center mx-auto mb-6">
                  <ChefHat className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#5C4033] mb-4">Fully Equipped Kitchen</h3>
                <p className="text-gray-600 leading-relaxed">
                  Two fully equipped kitchens with all modern facilities and live counter options in the buffet area.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-[#FFF8F0] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Car className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#5C4033] mb-4">Ample Parking</h3>
                <p className="text-gray-600 leading-relaxed">
                  In-campus parking facility for 350 cars with secure and well-organized parking management.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-[#FFF8F0] rounded-full flex items-center justify-center mx-auto mb-6">
                  <HomeIcon className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#5C4033] mb-4">Built up Area</h3>
                <p className="text-gray-600 leading-relaxed">
                  A built up area of over 1,00,000 sq ft providing expansive space for all your celebration needs.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-[#FFF8F0]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#5C4033] mb-4">Gallery</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {displayedImages.map((image, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-lg shadow-lg group cursor-pointer relative h-64"
                onClick={() => openLightbox(index)}
              >
                <OptimizedImage
                  src={image.src}
                  alt={image.alt}
                  width={400}
                  height={256}
                  quality={65}
                  className="w-full h-full transform group-hover:scale-110 transition-transform duration-500"
                  priority={index < 3}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                  <ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </div>
            ))}
          </div>

          {!showAllImages && (
            <div className="text-center mt-12">
              <Button
                onClick={() => setShowAllImages(true)}
                className="bg-[#5C4033] hover:bg-[#4A3329] text-white font-semibold px-8 py-6 text-lg transition-all duration-300"
              >
                View All {galleryImages.length} Images
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center">
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-[10000] text-white hover:text-[#D4AF37] transition-colors"
            aria-label="Close"
          >
            <X className="w-10 h-10" />
          </button>

          {/* Previous Button */}
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-[10000] text-white hover:text-[#D4AF37] transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-12 h-12" />
          </button>

          {/* Next Button */}
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-[10000] text-white hover:text-[#D4AF37] transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-12 h-12" />
          </button>

          {/* Image */}
          <div className="max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center p-4">
            <img
              src={optimizeImage(galleryImages[currentImageIndex].src, { width: 1200, height: 800, quality: 85 })}
              alt={galleryImages[currentImageIndex].alt}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>

          {/* Image Title - Bottom Left */}
          <div className="absolute bottom-4 left-4 text-white">
            <p className="text-2xl font-bold text-[#D4AF37]">{galleryImages[currentImageIndex].alt}</p>
          </div>

          {/* Image Counter - Bottom Center */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-lg font-semibold">
            {currentImageIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}

      {/* Contact Section */}
      <section id="contact" ref={contactSectionRef} className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl font-bold text-[#5C4033] mb-6">Get In Touch</h2>
              <p className="text-gray-600 text-lg mb-8">
                Ready to book your special event? Contact us today to check availability and discuss
                your requirements.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#5C4033] mb-1">Address</h4>
                    <a href="https://maps.app.goo.gl/82MtnVWZNqr7DAqt9" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#D4AF37] transition-colors">
                      No 29, Eachanari to Chettipalayam Road,<br />
                      Eachanari, Coimbatore - 641021
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#5C4033] mb-1">Phone</h4>
                    <a href="tel:+919688549999" className="text-gray-600 hover:text-[#D4AF37] transition-colors">
                      +91 96885 49999
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#5C4033] mb-1">Email</h4>
                    <p className="text-gray-600">info@selvammahaal.in</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#5C4033] mb-1">Working Hours</h4>
                    <p className="text-gray-600">10:00 AM - 08:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Card className="border-none shadow-xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-[#5C4033] mb-6">Send us a Message</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="Enter your name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="Enter your email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="Enter your phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="date">Event Date</Label>
                      <Input
                        id="date"
                        name="date"
                        type="date"
                        value={formData.date}
                        onChange={handleChange}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="mt-1"
                        placeholder="Tell us about your event"
                      />
                    </div>
                    <div>
                      <Label>Security Check *</Label>
                      <div className="mt-2 min-h-[78px]">
                        {showCaptcha ? (
                          <HCaptcha
                            ref={hcaptchaRef}
                            sitekey={HCAPTCHA_SITE_KEY}
                            onVerify={(token) => setHcaptchaToken(token)}
                            onExpire={() => setHcaptchaToken('')}
                            onError={() => setHcaptchaToken('')}
                          />
                        ) : (
                          <div className="text-sm text-gray-500">Loading verification…</div>
                        )}
                      </div>
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-[#5C4033] font-semibold py-6 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Submit Inquiry'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#5C4033] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <img
                  src={optimizeImage('https://customer-assets.emergentagent.com/job_6c6d7f31-c413-4b8b-8136-a6a0642b6410/artifacts/bkx7dojo_Logo%20White.png', { width: 140, quality: 80 })}
                  alt="Selvam Mahaal Logo"
                  width="140"
                  height="56"
                  loading="lazy"
                  decoding="async"
                  className="h-14 w-auto object-contain"
                />
              </div>
              <p className="text-gray-300 leading-relaxed">
                Creating memorable celebrations with exceptional service and world-class facilities in
                the heart of Coimbatore.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4 text-[#D4AF37]">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#home" className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                    Home
                  </a>
                </li>
                <li>
                  <a href="#about" className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#facilities" className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                    Facilities
                  </a>
                </li>
                <li>
                  <a href="#gallery" className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                    Gallery
                  </a>
                </li>
                <li>
                  <a href="#contact" className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                    Contact
                  </a>
                </li>
                <li>
                  <a href="https://booking.selvammahaal.in" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-[#D4AF37] transition-colors">
                    Booking Portal
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4 text-[#D4AF37]">Contact Info</h4>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center space-x-2">
                  <Phone className="w-5 h-5" />
                  <a href="tel:+919688549999" className="hover:text-[#D4AF37] transition-colors">
                    +91 96885 49999
                  </a>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-5 h-5" />
                  <span>info@selvammahaal.in</span>
                </li>
                <li className="flex items-start space-x-2">
                  <MapPin className="w-5 h-5 mt-1" />
                  <a href="https://maps.app.goo.gl/82MtnVWZNqr7DAqt9" target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors">
                    Eachanari, Coimbatore - 641021
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-600 pt-6 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Selvam Mahaal. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
